function clerkFrontendApi(publishableKey, globalScope) {
  const encodedDomain = String(publishableKey).split("_")[2];
  const decodedDomain = encodedDomain ? globalScope.atob(encodedDomain) : "";
  if (!decodedDomain.endsWith("$")) throw new Error("Clerk publishable key is invalid");
  return decodedDomain.slice(0, -1);
}

function loadClerkUiBundle(publishableKey, {
  document = globalThis.document,
  globalScope = globalThis.window,
} = {}) {
  if (globalScope.__internal_ClerkUICtor) return Promise.resolve(globalScope.__internal_ClerkUICtor);

  const script = document.createElement("script");
  script.dataset.clerkUi = "";
  script.async = true;
  script.crossOrigin = "anonymous";
  script.src = `https://${clerkFrontendApi(publishableKey, globalScope)}/npm/@clerk/ui@1/dist/ui.browser.js`;

  return new Promise((resolve, reject) => {
    script.addEventListener("load", () => {
      if (!globalScope.__internal_ClerkUICtor) {
        reject(new Error("Clerk UI bundle failed to load"));
        return;
      }
      resolve(globalScope.__internal_ClerkUICtor);
    }, { once: true });
    script.addEventListener("error", () => reject(new Error("Clerk UI bundle failed to load")), { once: true });
    document.head.append(script);
  });
}

async function loadProductionClerk(publishableKey) {
  const { Clerk } = await import("@clerk/clerk-js");
  const ClerkUI = await loadClerkUiBundle(publishableKey);
  const clerk = new Clerk(publishableKey);
  await clerk.load({ ui: { ClerkUI } });
  return clerk;
}

export function createAuthController({
  fetchImpl = globalThis.fetch,
  loadClerk = loadProductionClerk,
  accountContainer = null,
} = {}) {
  const subscribers = new Set();
  let clerk = null;
  let session = null;
  let user = null;
  let unsubscribe = null;
  let pendingAction = null;
  let started = false;
  let ready = false;
  let unavailable = false;

  const state = () => ({ ready, signedIn: Boolean(session && user), unavailable, user });

  async function applyState(next = {}) {
    const wasSignedIn = Boolean(session && user);
    session = next.session ?? clerk?.session ?? null;
    user = next.user ?? clerk?.user ?? null;
    const signedIn = Boolean(session && user);

    if (accountContainer) {
      if (signedIn && !wasSignedIn) clerk?.mountUserButton?.(accountContainer);
      if (!signedIn && wasSignedIn) {
        clerk?.unmountUserButton?.(accountContainer);
        accountContainer.replaceChildren();
      }
    }
    if (wasSignedIn && !signedIn) pendingAction = null;
    await Promise.all([...subscribers].map((subscriber) => subscriber(state())));

    if (signedIn && pendingAction) {
      const action = pendingAction;
      pendingAction = null;
      await action();
    }
  }

  return {
    async start() {
      if (started) return state();
      const response = await fetchImpl("/api/config", { headers: { Accept: "application/json" } });
      if (!response.ok) throw new Error("Account configuration is unavailable");
      const config = await response.json();
      if (!config?.clerkPublishableKey) throw new Error("Clerk publishable key is missing");
      clerk = await loadClerk(config.clerkPublishableKey);
      unsubscribe = clerk.addListener?.((next) => applyState(next)) ?? null;
      started = true;
      ready = true;
      unavailable = false;
      await applyState({ session: clerk.session, user: clerk.user });
      return state();
    },

    isSignedIn() {
      return Boolean(session && user);
    },

    async getToken() {
      return session?.getToken ? session.getToken() : null;
    },

    async requireUser(action, { force = false } = {}) {
      if (!force && this.isSignedIn()) return action?.();
      if (action) pendingAction = action;
      await clerk?.openSignIn?.();
      return undefined;
    },

    subscribe(subscriber) {
      subscribers.add(subscriber);
      subscriber(state());
      return () => subscribers.delete(subscriber);
    },

    async signOut() {
      pendingAction = null;
      await clerk?.signOut?.();
    },

    async setUnavailable() {
      ready = true;
      unavailable = true;
      await Promise.all([...subscribers].map((subscriber) => subscriber(state())));
      return state();
    },

    destroy() {
      unsubscribe?.();
      subscribers.clear();
      pendingAction = null;
    },
  };
}
